import classNames from "classnames";
import { useCheckPremium } from "utils";
import React, { memo, ReactElement, useState } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";
import crownGreen from "assets/images/crownGreen.svg";
import config from "config";
import { usePendingCount } from "modules/AdminDashboard/AdminDashboard.queries";
import { APP_ROUTES } from "const";
import HubSpotForm from "components/HubSpotForm";
import ReactTooltip from "react-tooltip";

export interface IProps extends React.HTMLProps<HTMLDivElement> {
  title: string;
  icon: string;
  iconActive: string;
  className?: string;
  to?: string;
  tabs?: APP_ROUTES[];
  showNavigation?: boolean;
  navigateOutside?: boolean;
  disableForFreeUsers?: boolean;
  dataTip?: string;
}

function NavigationItem({
  icon,
  iconActive,
  className,
  title,
  to = "",
  tabs,
  showNavigation,
  navigateOutside,
  disableForFreeUsers = false,
  dataTip,
}: IProps) {
  const location = useLocation();
  const [showForm, setShowForm] = useState(false);

  const match =
    matchPath({ path: to, end: false }, location.pathname) ||
    tabs?.some((path) => matchPath({ path, end: false }, location.pathname));

  const { data } = usePendingCount();

  let pendingCountsBlock: ReactElement = <></>;

  if (title === "Admin Dashboard" && data?.pendingCount) {
    pendingCountsBlock = (
      <span
        className={classNames(
          "flex text-[10px] items-center text-center justify-center text-[#FFFFFF] bg-[#28C18A] rounded-full mx-2 w-4 h-4",
          {
            "!text-[8px]": data?.pendingCount > 99,
          }
        )}
      >
        {data?.pendingCount > 99 ? "99+" : data?.pendingCount}
      </span>
    );
  }

  const isPremium = useCheckPremium();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <>
      <ReactTooltip
        id={`navigation-item-${title}`}
        delayShow={300}
        effect="solid"
        place="right"
        className="max-w-[200px] p-1 text-[12px] text-center rounded !opacity-100"
      />
      <div
        data-tip={dataTip}
        data-for={`navigation-item-${title}`}
        onMouseEnter={() => {
          setIsHovered(true);
          ReactTooltip.hide();
        }}
        onMouseLeave={() => {
          setIsHovered(false);
          ReactTooltip.hide();
        }}
        onClick={() => {
          setIsHovered(false);
          ReactTooltip.hide();
        }}
        className={classNames(
          "flex relative navigation-item flex-nowrap bg-tablePrimary h-[46px] hover:bg-[#31414ec2]",
          className,
          { "bg-primary": !match, "bg-[#31414E]": match }
        )}
        style={{
          backgroundColor:
            match || isHovered ? config.BACKGROUND_COLOR : undefined,
        }}
      >
        {navigateOutside === true ? (
          <a
            href={to}
            target="_blank"
            rel="noreferrer"
            className="flex items-center pl-6 w-[100%]"
          >
            <img
              src={match ? iconActive : icon}
              alt="mn"
              className={classNames("w-[18px]", {})}
            />
            <span
              className={classNames(
                "ml-[11px] whitespace-nowrap text-xs font-normal hover:text-white flex content-center flex-wrap h-[100%] w-[100%]",
                {
                  "innerViewportSpecific:hidden": !showNavigation,
                  "text-white": match,
                  "text-[#535A60] dark:text-secondary": !match,
                }
              )}
              style={{
                color: match || isHovered ? "white" : undefined,
              }}
            >
              {title}
            </span>
          </a>
        ) : disableForFreeUsers && !isPremium ? (
          <div
            onClick={() => {
              setShowForm(true);
            }}
            className="cursor-pointer flex items-center pl-6 w-[100%] hover:text-white"
          >
            <img src={crownGreen} alt="crownGreen" className="w-[18px]" />
            <span className="text-secondary items-center ml-[11px] w-[100%] whitespace-nowrap text-xs font-normal hover:text-white flex content-center flex-wrap h-[100%]">
              {title}
            </span>
          </div>
        ) : (
          <Link
            to={to}
            className={classNames(
              "flex items-center pl-6 w-[100%] hover:text-white"
            )}
          >
            <img
              src={match ? iconActive : icon}
              alt="mn"
              className={classNames(
                "w-[16px] h-[16px] aspect-square object-contain block"
              )}
            />
            <span
              className={classNames(
                "items-center ml-[11px] w-[100%] whitespace-nowrap text-xs font-normal hover:text-white flex content-center flex-wrap h-[100%]",
                {
                  "innerViewportSpecific:hidden": !showNavigation,
                  "text-white": match,
                  "text-[#535A60] dark:text-secondary": !match,
                }
              )}
              style={{
                color: match || isHovered ? "white" : undefined,
              }}
            >
              {title} {pendingCountsBlock}
            </span>
          </Link>
        )}
      </div>

      <HubSpotForm setShow={setShowForm} show={showForm} />
    </>
  );
}

export default memo(NavigationItem);
